// CatalogPage.jsx

import React, { useState, useEffect, useContext } from 'react';
import {
    Container,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    CircularProgress,
    TextField,
    Box,
    Pagination,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ConfigContext, http } from '../m34n-ui/src/lib.js'; // Adjust the import path as needed

const CatalogPage = () => {
    const { config } = useContext(ConfigContext);
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(0); // **Start at 0**
    const [pageSize] = useState(10); // Number of products per page
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch products whenever pageNumber or searchQuery changes
    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            setError(null);
            try {
                // Build query parameters
                const params = {
                    pageSize,
                    pageNumber,
                };
                if (searchQuery.trim() !== '') {
                    params.name = searchQuery.trim();
                }

                // Make the API call
                const response = await http.get({}, `${config.uri.base}/catalog/product`, { params });

                // Check if response has the expected structure
                if (response && Array.isArray(response.rows) && typeof response.totalCount === 'number') {
                    setProducts(response.rows);
                    setTotalCount(response.totalCount);
                } else {
                    throw new Error('Unexpected response structure from the API.');
                }
            } catch (err) {
                console.error('Error fetching products:', err);
                setError('Failed to load products. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [config.uri.base, pageNumber, pageSize, searchQuery]);

    // Handle page change
    const handlePageChange = (event, value) => {
        setPageNumber(value - 1); // **Convert 1-based UI page to 0-based internal pageNumber**
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top on page change
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPageNumber(0); // **Reset to first page (0) on new search**
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalCount / pageSize);

    // Handle product click
    const handleProductClick = (id) => {
        navigate(`/catalog/product/${id}`);
    };

    return (
        <Box disableGutters>
            {/* Header Bar */}
            <Box
                sx={{
                    height: '110px',
                    backgroundColor: '#d2dfe4ff',
                    display: 'flex',
                    alignItems: 'center',
                    paddingX: 4, // Horizontal padding
                    boxSizing: 'border-box',
                }}
            >
                {/* Placeholder Logo */}
                <Box
                    component="img"
                    src="/login_header.png"
                    alt="Logo Placeholder"
                    sx={{
                        height: '40px',
                        width: 'auto',
                        // You can adjust the size as needed
                    }}
                />
                {/* Optional: Add additional elements like navigation links here */}
            </Box>

            {/* Main Content */}
            <Box sx={{ padding: 4 }}>
                <Box my={4} textAlign="center">
                    <Typography variant="h4">Product Catalog</Typography>
                </Box>

                {/* Search Bar */}
                <Box mb={4} display="flex" justifyContent="center">
                    <TextField
                        label="Search Products"
                        variant="outlined"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        fullWidth
                        maxWidth="400px"
                    />
                </Box>

                {/* Loading Indicator */}
                {loading && (
                    <Box textAlign="center" my={4}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error Message */}
                {error && (
                    <Box textAlign="center" my={4}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {/* Product Grid */}
                {!loading && !error && products.length > 0 && (
                    <Grid container spacing={4}>
                        {products.map((product) => (
                            <Grid item key={product._id.$oid} xs={12} sm={6} md={4} lg={3}>
                                <Card
                                    onClick={() => handleProductClick(product._id)}
                                    sx={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={product.thumbnailURL}
                                        alt={product.name}
                                    />
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography gutterBottom variant="h6" component="div">
                                            {product.name}
                                        </Typography>

                                        {/* Display Registration Types */}
                                        {Array.isArray(product.registrationTypes) && product.registrationTypes.length > 0 ? (
                                            <List dense>
                                                {product.registrationTypes.map((regType, index) => (
                                                    <ListItem key={index} disableGutters>
                                                        <ListItemText
                                                            primary={regType.name}
                                                            secondary={`$${parseFloat(regType.price).toFixed(2)}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body1" color="text.primary">
                                                ${parseFloat(product.price).toFixed(2)}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* No Products Found */}
                {!loading && !error && products.length === 0 && (
                    <Box textAlign="center" my={4}>
                        <Typography>No products found.</Typography>
                    </Box>
                )}

                {/* Pagination */}
                {!loading && !error && totalPages > 1 && (
                    <Box my={4} display="flex" justifyContent="center">
                        <Pagination
                            count={totalPages}
                            page={pageNumber + 1} // **Convert 0-based internal pageNumber to 1-based UI page**
                            onChange={handlePageChange}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CatalogPage;

